import {useCallback, useEffect, useState} from "react";
import {Page} from "../pages";
import {defineMessage, FormattedMessage} from "react-intl";
import {Divider, Typography} from "@mui/material";
import SearchDomainByName from "./SearchDomainByName";
import SearchDomainByE164 from "./SearchDomainByE164";
import MemoDomainStatus from "./MemoDomainStatus";
import {Domain} from "./types";
import {Link} from "../widgets";
import {useLocation, useNavigate} from "react-router";
import {DomainFetcherFactoryContextProvider, useDomainListFetcher} from "./domainFetcher";

const useExternalDomain = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const externalDomainId = parseInt(new URLSearchParams(location.search).get("domainId"));

    const changeExternalDomainId = (id: number) => {
        const newLocation = structuredClone(location);
        newLocation.search = id ? `?domainId=${id}` : "";
        navigate(newLocation);
    };

    return {
        externalDomainId,
        changeExternalDomainId,
    };
};

const EndpointStatusPage = () => {
    const [domain, setDomain] = useState<Domain>();
    const {fetch, domains} = useDomainListFetcher();

    const {externalDomainId, changeExternalDomainId} = useExternalDomain();

    const setDomainFromList = useCallback(
        (domainList: Domain[]) => {
            const extDomain = domainList.filter((d) => d.id === externalDomainId)[0];
            if (extDomain) setDomain(extDomain);
        },
        [externalDomainId],
    );

    const setDomainFromExternalSource = useCallback(async () => {
        const fetchedDomains = await fetch();
        setDomainFromList(fetchedDomains);
    }, [fetch, setDomainFromList]);

    useEffect(() => {
        if (externalDomainId) setDomainFromExternalSource();
    }, [externalDomainId, setDomainFromExternalSource]);

    const updateDomain = (d: Domain) => {
        setDomain(d);
        changeExternalDomainId(d.id);
    };

    useEffect(() => {
        if (externalDomainId !== domain?.id) setDomainFromList(domains);
    }, [domain?.id, domains, externalDomainId, setDomainFromList]);

    return (
        <Page title={defineMessage({id: "endpointStatus.title", defaultMessage: "Endpoint status"})}>
            <div>
                <Typography style={{margin: "10px 0"}}>
                    <FormattedMessage id="endpointStatus.legend" defaultMessage="Select domain by name or E.164" />
                </Typography>
                <div style={{display: "flex"}}>
                    <SearchDomainByName onChange={updateDomain} />
                    <SearchDomainByE164 onChange={updateDomain} style={{marginLeft: 10}} />
                </div>
            </div>
            {domain && (
                <>
                    <Divider style={{marginTop: 15}} />
                    <Typography variant="h6" style={{margin: "10px 0"}}>
                        <FormattedMessage
                            id="endpointStatus.domainTitle"
                            defaultMessage="Endpoint status for domain: {name}"
                            values={{
                                name: <Link to={`/domains/${domain?.id}`}>{domain?.name}</Link>,
                            }}
                        />
                    </Typography>
                    <MemoDomainStatus domainId={domain?.id} />
                </>
            )}
        </Page>
    );
};

export default function EndpointStatus() {
    return (
        <DomainFetcherFactoryContextProvider>
            <EndpointStatusPage />
        </DomainFetcherFactoryContextProvider>
    );
}
